import { financialYearOf } from './financial-year.js';

/**
 * The invoice number, which has to survive an audit.
 *
 * The law's requirement is short and unforgiving: a tax invoice number is unique per supplier
 * per financial year, sequential, and no more than sixteen characters of letters, digits, `/`
 * and `-`. "Sequential" is read by an assessing officer as GAPLESS — a missing number invites
 * the question of what document used to be there — which is why the sequence is drawn from an
 * atomic counter INSIDE the transaction that writes the invoice (see `nextSequence`): an
 * invoice that fails to write returns its number by never having taken it.
 *
 * Credit notes carry their own series. Mixing them into the invoice series would break the
 * invoice sequence the moment a correction is issued, and GSTR-1 reports the two separately
 * anyway.
 *
 * The shape a shop actually sees is `BQ/25-26/0001`. The prefix is the shop's, the year is the
 * financial year the document was issued in, and the serial restarts at 1 each 1 April.
 */

/** How the financial year is written inside a document number. */
export const INVOICE_YEAR_STYLES = ['short', 'full'] as const;
export type InvoiceYearStyle = (typeof INVOICE_YEAR_STYLES)[number];

export const DEFAULT_INVOICE_PREFIX = 'INV';
export const DEFAULT_CREDIT_NOTE_PREFIX = 'CN';
export const DEFAULT_INVOICE_YEAR_STYLE: InvoiceYearStyle = 'short';

/** The GST cap on a document number, counted over the whole string. */
export const MAX_DOCUMENT_NUMBER_LENGTH = 16;

/** `2026-27` → `26-27`, which is how it is written on a bill. */
export function shortFinancialYear(financialYear: string): string {
  return financialYear.startsWith('20') ? financialYear.slice(2) : financialYear;
}

export function financialYearLabel(financialYear: string, style: InvoiceYearStyle): string {
  return style === 'short' ? shortFinancialYear(financialYear) : financialYear;
}

/** The two series a shop runs, each gapless within its own financial year. */
export const DOCUMENT_SERIES = ['invoice', 'credit_note'] as const;
export type DocumentSeries = (typeof DOCUMENT_SERIES)[number];

/**
 * The counter key a number is drawn from: one sequence per shop, per series, per year.
 *
 * Note that this is keyed on the ISSUE date's financial year, not the order's. An order booked
 * on 30 March and billed on 2 April is billed in the new year's series, which is what the
 * document date says and therefore what the return has to agree with.
 */
export function invoiceCounterKey(series: DocumentSeries, issuedAt: Date): string {
  return `${series}:${financialYearOf(issuedAt)}`;
}

/**
 * `BQ/25-26/0001`.
 *
 * Four digits of padding, so a year's documents sort lexically as well as numerically, and no
 * truncation past that: a boutique that issues more than 9,999 invoices in a year gets a
 * five-digit serial rather than a duplicate number.
 */
export function formatDocumentNumber(
  prefix: string,
  financialYear: string,
  sequence: number,
  style: InvoiceYearStyle = DEFAULT_INVOICE_YEAR_STYLE,
): string {
  return `${prefix}/${financialYearLabel(financialYear, style)}/${String(sequence).padStart(4, '0')}`;
}

/**
 * True when a shop's chosen prefix still leaves room for the year and a four-digit serial
 * inside the sixteen-character limit. The settings screen checks this before saving, because
 * discovering it at the counter — with a customer waiting for a bill — is far too late.
 */
export function documentNumberFits(
  prefix: string,
  style: InvoiceYearStyle = DEFAULT_INVOICE_YEAR_STYLE,
): boolean {
  return formatDocumentNumber(prefix, '2026-27', 1, style).length <= MAX_DOCUMENT_NUMBER_LENGTH;
}
